import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ALL_TUTORIAL_MODULES, TutorialModule } from './tutorial-steps.config';

export interface TutorialStep {
  selector: string;
  titleKey: string;
  descriptionKey: string;
  position: 'top' | 'bottom' | 'left' | 'right';
  /** If true, tutorial won't automatically skip this step if element is missing. */
  mandatory?: boolean;
}

export interface UserContext {
  role: string;
  type?: string;         // 'Paramedic', 'Caretaker', etc.
  privilege?: string;    // semicolon-separated
  isIndividual?: boolean;
}

@Injectable({ providedIn: 'root' })
export class TutorialService {
  private readonly STORAGE_PREFIX = 'tutorial_completed';

  showDialog$ = new BehaviorSubject<boolean>(false);
  active$ = new BehaviorSubject<boolean>(false);
  currentStep$ = new BehaviorSubject<number>(0);
  currentModuleId$ = new BehaviorSubject<string>('');
  steps: TutorialStep[] = [];

  private userContext: UserContext = { role: 'ROLE_API_MANAGER' };
  private currentRoute = '';
  private pageCheckTimer: any = null;

  constructor(
    private translate: TranslateService,
    private router: Router
  ) {
    // Track route changes for page-specific tutorials
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe((e: NavigationEnd) => {
      this.currentRoute = e.urlAfterRedirects || e.url;
      this.checkPageTutorial();
    });
  }

  /**
   * Set user context for role/feature-based filtering.
   * Call this when user logs in or user data is loaded.
   */
  setUserContext(ctx: UserContext): void {
    this.userContext = ctx;
  }

  /**
   * Legacy: check first login (shows onboarding dialog).
   */
  checkFirstLogin(): void {
    const onboardingModule = this.getOnboardingModule();
    if (!onboardingModule) return;

    const completed = localStorage.getItem(this.getStorageKey(onboardingModule.id));
    if (!completed) {
      setTimeout(() => this.showDialog$.next(true), 1500);
    }
  }

  startTutorial(role: string): void {
    this.showDialog$.next(false);
    const module = this.getOnboardingModule();
    if (module) {
      this.startModule(module);
    }
  }

  startModuleById(moduleId: string): void {
    const module = ALL_TUTORIAL_MODULES.find(m => m.id === moduleId);
    if (module) {
      this.startModule(module);
    }
  }

  startPageTutorial(): void {
    // Ensure currentRoute is up-to-date (fallback to router.url)
    const route = this.currentRoute || this.router.url.split('?')[0].split('#')[0];
    const module = this.findPageModule(route);
    if (module) {
      this.resetModule(module.id);
      this.startModule(module);
    }
  }

  private startModule(module: TutorialModule): void {
    this.steps = module.steps;
    this.currentModuleId$.next(module.id);
    if (this.steps.length > 0) {
      this.currentStep$.next(0);
      this.active$.next(true);
    }
  }

  nextStep(): void {
    const current = this.currentStep$.value;
    if (current < this.steps.length - 1) {
      this.currentStep$.next(current + 1);
    } else {
      this.completeTutorial();
    }
  }

  prevStep(): void {
    const current = this.currentStep$.value;
    if (current > 0) {
      this.currentStep$.next(current - 1);
    }
  }

  skipTutorial(): void {
    this.completeTutorial();
  }

  dismissDialog(): void {
    this.showDialog$.next(false);
    const module = this.getOnboardingModule();
    if (module) {
      localStorage.setItem(this.getStorageKey(module.id), 'true');
    }
  }

  private completeTutorial(): void {
    const moduleId = this.currentModuleId$.value;
    this.active$.next(false);
    this.currentStep$.next(0);
    if (moduleId) {
      localStorage.setItem(this.getStorageKey(moduleId), 'true');
    }
  }

  resetModule(moduleId: string): void {
    localStorage.removeItem(this.getStorageKey(moduleId));
  }

  resetTutorial(): void {
    const module = this.getOnboardingModule();
    if (module) {
      localStorage.removeItem(this.getStorageKey(module.id));
    }
  }

  resetAllTutorials(): void {
    ALL_TUTORIAL_MODULES.forEach(m => {
      localStorage.removeItem(this.getStorageKey(m.id));
    });
    // Legacy key
    localStorage.removeItem('tutorial_completed');
  }

  isModuleCompleted(moduleId: string): boolean {
    return localStorage.getItem(this.getStorageKey(moduleId)) === 'true';
  }

  getAvailableModules(route?: string): TutorialModule[] {
    return ALL_TUTORIAL_MODULES.filter(m => this.isModuleAvailable(m, route));
  }

  /**
   * Get page-specific modules for the current route, ordered by most-specific routePrefix first.
   */
  getPageModules(): TutorialModule[] {
    return ALL_TUTORIAL_MODULES
      .filter(m =>
        m.routePrefix &&
        this.currentRoute.startsWith(m.routePrefix) &&
        this.isModuleAvailable(m)
      )
      .sort((a, b) => b.routePrefix.length - a.routePrefix.length);
  }

  // --- Private helpers ---

  private getStorageKey(moduleId: string): string {
    return `${this.STORAGE_PREFIX}_${moduleId}`;
  }

  private getOnboardingModule(): TutorialModule | undefined {
    const role = this.userContext.role;
    if (role === 'ROLE_API_MANAGER') {
      return ALL_TUTORIAL_MODULES.find(m => m.id === 'onboarding_manager');
    } else if (role === 'ROLE_API_CAREGIVER') {
      return ALL_TUTORIAL_MODULES.find(m => m.id === 'onboarding_caregiver');
    } else {
      return ALL_TUTORIAL_MODULES.find(m => m.id === 'onboarding_user');
    }
  }

  private findPageModule(route: string): TutorialModule | undefined {
    // Find the most specific matching module
    let bestMatch: TutorialModule | undefined;
    let bestLength = 0;

    for (const module of ALL_TUTORIAL_MODULES) {
      if (!module.routePrefix) continue;
      const routeClean = route.split('?')[0].split('#')[0];
      if (!routeClean.startsWith(module.routePrefix)) continue;
      if (!this.isModuleAvailable(module)) {
        continue;
      }

      if (module.routePrefix.length > bestLength) {
        bestLength = module.routePrefix.length;
        bestMatch = module;
      }
    }

    return bestMatch;
  }

  private isModuleAvailable(module: TutorialModule, route?: string): boolean {
    const ctx = this.userContext;

    // Check role
    if (module.roles && module.roles.length > 0) {
      if (!module.roles.includes(ctx.role)) return false;
    }

    // Check user type
    if (module.userTypes && module.userTypes.length > 0) {
      if (!ctx.type || !module.userTypes.includes(ctx.type)) return false;
    }

    // Check privileges
    if (module.privileges && module.privileges.length > 0) {
      if (!ctx.privilege) return false;
      const hasPrivilege = module.privileges.some(p => ctx.privilege.includes(p));
      if (!hasPrivilege) return false;
    }

    // Check individual
    if (module.requireIndividual && !ctx.isIndividual) return false;

    // Check route if specified
    if (route && module.routePrefix) {
      if (!route.startsWith(module.routePrefix)) return false;
    }

    return true;
  }

  /**
   * Auto-check page tutorial on navigation.
   * Delayed to let the page render first.
   */
  private checkPageTutorial(): void {
    if (this.pageCheckTimer) {
      clearTimeout(this.pageCheckTimer);
    }
    this.pageCheckTimer = setTimeout(() => {
      if (this.active$.value) return; // Don't interrupt active tutorial
      if (this.showDialog$.value) return; // Don't start if onboarding dialog is shown

      const module = this.findPageModule(this.currentRoute);
      if (module && !this.isModuleCompleted(module.id)) {
        this.startModule(module);
      }
    }, 2000);
  }
}
